import React, { useReducer, useEffect, useCallback, useState } from "react";
import { Modal, Button, Form, Spinner, CloseButton } from "react-bootstrap";
import { toast } from "react-toastify";

const initialState = { questionText: "", active: true };

const formReducer = (state, action) => {
  switch (action.type) {
    case "UPDATE_FIELD":
      return { ...state, [action.name]: action.value };
    case "TOGGLE_ACTIVE":
      return { ...state, active: !state.active };
    case "SET_FORM":
      return { ...action.payload };
    case "RESET":
      return initialState;
    default:
      return state;
  }
};

const EditSecurityQuestionModal = ({
  show,
  onClose,
  selectedQuestion,
  updateSecurityQuestion,
  loading = false,
  fetchSecurityQuestions, // ← make sure you pass this from parent!
}) => {
  const [formData, dispatch] = useReducer(formReducer, initialState);
  const [submitting, setSubmitting] = useState(false);

  // Populate form when selectedQuestion changes
  useEffect(() => {
    if (selectedQuestion) {
      dispatch({
        type: "SET_FORM",
        payload: {
          questionText: selectedQuestion.questionText || "",
          active: selectedQuestion.active !== undefined ? selectedQuestion.active : true,
        },
      });
    } else {
      dispatch({ type: "RESET" });
    }
  }, [selectedQuestion]);

  const handleClose = useCallback(() => {
    onClose();
    dispatch({ type: "RESET" });
  }, [onClose]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    dispatch({ type: "UPDATE_FIELD", name, value });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.questionText.trim()) {
      toast.error("Question text is required");
      return;
    }

    setSubmitting(true);

    try {
      await updateSecurityQuestion(selectedQuestion.id, formData);

      // SUCCESS TOAST — this is the only thing added
      toast.success("Security question updated successfully!", {
        position: "top-right",
        autoClose: 3000,
      });

      await fetchSecurityQuestions?.(); // refresh list
      handleClose();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to update security question"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} keyboard={!submitting} backdrop="static" centered>
      <Modal.Header className="bg-body-tertiary border-0">
        <Modal.Title style={{ fontSize: 14 }} className="ps-2">
          Edit Security Question
        </Modal.Title>
        <CloseButton
          className="btn btn-circle btn-sm transition-base p-0"
          onClick={handleClose}
          disabled={submitting}
        />
      </Modal.Header>

      <Modal.Body className="px-4 py-0 pb-3">
        {loading && !selectedQuestion ? (
          <div className="d-flex justify-content-center align-items-center" style={{ height: 120 }}>
            <Spinner animation="border" variant="success" />
          </div>
        ) : (
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Question Text</Form.Label>
              <Form.Control
                type="text"
                name="questionText"
                placeholder="Enter security question"
                value={formData.questionText}
                onChange={handleChange}
                disabled={submitting}
                required
                autoFocus
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="active-switch"
                label={formData.active ? "Active (visible)" : "Inactive (hidden)"}
                checked={formData.active}
                onChange={() => dispatch({ type: "TOGGLE_ACTIVE" })}
                disabled={submitting}
              />
            </Form.Group>

            <div className="d-flex gap-2 mt-4">
              <Button variant="secondary" onClick={handleClose} disabled={submitting}>
                Cancel
              </Button>
              {submitting ? (
                <Spinner animation="border" size="md" variant="success" />
              ) : (
                <Button type="submit" variant="success">
                  Save Changes
                </Button>
              )}
            </div>
          </Form>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default EditSecurityQuestionModal;